import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore';
import { BUDGET_DOC_ID } from '@finvox/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getFirestoreDb } from '@/lib/firebase';
import { shouldSkipOrgLiveUpdate } from '@/lib/orgSnapshotGuard';
import { useAsyncSave } from '@/components/settings/useAsyncSave';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { SettingsFirestoreError } from '@/components/settings/SettingsFirestoreError';
import { Button } from '@/components/ui/Button';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Spinner } from '@/components/ui/Spinner';
import { SettingsSection } from '@/components/settings/SettingsSection';

type Account = { id: string; code: string; name: string; type: string };
type BudgetLine = { accountId: string; amount: string };

const PL_TYPES = new Set(['revenue', 'expense', 'cogs']);

function BudgetInner({ visible }: { visible: boolean }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { orgId, periodId, online, canEdit, periodClosed } = useSettingsAccess();
  const { saving, run } = useAsyncSave('settings-budget');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [budgetError, setBudgetError] = useState<string | null>(null);
  const [lines, setLines] = useState<BudgetLine[]>([]);

  const editable = canEdit && !periodClosed;

  useEffect(() => {
    if (!orgId || !visible || !user?.uid) return;
    const uid = user.uid;
    setAccountsLoading(true);
    setAccountsError(null);
    return onSnapshot(
      query(collection(getFirestoreDb(), 'organizations', orgId, 'accounts'), orderBy('code')),
      (snap) => {
        if (shouldSkipOrgLiveUpdate(snap.metadata, uid, orgId)) return;
        setAccounts(
          snap.docs
            .map((d) => ({
              id: d.id,
              code: String(d.data().code ?? ''),
              name: String(d.data().name ?? ''),
              type: String(d.data().type ?? '')
            }))
            .filter((a) => PL_TYPES.has(a.type))
        );
        setAccountsLoading(false);
      },
      (e) => {
        setAccountsError(e.message);
        setAccounts([]);
        setAccountsLoading(false);
      }
    );
  }, [orgId, visible, user?.uid]);

  useEffect(() => {
    if (!orgId || !periodId || !visible || !user?.uid) return;
    const uid = user.uid;
    return onSnapshot(
      doc(getFirestoreDb(), 'organizations', orgId, 'fiscalPeriods', periodId, 'budget', BUDGET_DOC_ID),
      (snap) => {
        if (shouldSkipOrgLiveUpdate(snap.metadata, uid, orgId)) return;
        const raw = snap.data()?.lines;
        if (Array.isArray(raw)) {
          setLines(
            raw.map((r: { accountId?: string; amount?: string }) => ({
              accountId: String(r.accountId ?? ''),
              amount: String(r.amount ?? '0.00')
            }))
          );
        } else setLines([]);
      },
      (e) => setBudgetError(e.message)
    );
  }, [orgId, periodId, visible, user?.uid]);

  function amountFor(accountId: string): string {
    return lines.find((l) => l.accountId === accountId)?.amount ?? '';
  }

  function setAmount(accountId: string, amount: string) {
    setLines((prev) => {
      const rest = prev.filter((l) => l.accountId !== accountId);
      if (!amount.trim()) return rest;
      return [...rest, { accountId, amount }];
    });
  }

  async function save() {
    if (!orgId || !periodId || !editable || !online) return;
    await run(async () => {
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'fiscalPeriods', periodId, 'budget', BUDGET_DOC_ID),
        {
          lines: lines.filter((l) => l.amount && l.accountId),
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    }, t('settings.budgetSaved'));
  }

  return (
    <>
      <SettingsFirestoreError message={budgetError ?? undefined} />
      <div className="relative max-h-64 overflow-y-auto rounded-[var(--fv-radius)] border border-[var(--fv-border)]">
        {accountsLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--fv-surface)]/70">
            <Spinner size="md" />
          </div>
        )}
        {accountsError && <p className="px-3 py-2 text-red-600">{accountsError}</p>}
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[var(--fv-bg)]">
            <tr>
              <th className="p-2 text-start font-medium">{t('coa.code')}</th>
              <th className="p-2 text-end font-medium">{t('settings.budgetAmount')}</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr
                key={a.id}
                className="border-t border-[var(--fv-border)] transition-colors hover:bg-[var(--fv-bg)]"
              >
                <td className="p-2">
                  {a.code} — {a.name}
                </td>
                <td className="p-2">
                  <MoneyInput
                    className="w-full"
                    allowEmpty
                    disabled={!editable || !online}
                    value={amountFor(a.id)}
                    onValueChange={(v) => setAmount(a.id, v)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable && (
        <Button variant="secondary" disabled={!online || saving} onClick={() => void save()}>
          {t('common.save')}
        </Button>
      )}
    </>
  );
}

export function BudgetSection() {
  const { t } = useTranslation();
  const { periodClosed } = useSettingsAccess();

  return (
    <SettingsSection
      id="settings-budget"
      lazy
      title={t('settings.budget')}
      description={periodClosed ? t('settings.budgetClosedHint') : t('settings.budgetHint')}
    >
      {(visible) => (visible ? <BudgetInner visible={visible} /> : null)}
    </SettingsSection>
  );
}
